// The windows on a screen (Snap and Look, core/render-dom.js), for a screen reader and a keyboard (Josh 10-02: "make
// sure all screens and buttons … are accessible"). The window modules draw for the headset, where a tap is a gaze and a
// pinch; on a page the same DOM is read aloud and tabbed through, so as each window draws, its controls get:
//   - a name: an icon-only control's `title` becomes its `aria-label` (a field's placeholder, when it has nothing else)
//   - a role: a tappable `[data-act]` that is not a button, link or field is a `button`, reachable with Tab, pressed
//     with Return or Space
//   - pictures inside a control that already says what it is are left out of what is read (`alt=""`)
// Nothing about how anything looks; the focus ring is core/screen.css.
//
// One seam: `screenA11y(root)` — watches `root` for as long as the page lives.
const NATIVE = 'button, a, input, textarea, select, label, summary'

function tidy(el) {
  if (el.nodeType !== 1) return
  const all = el.matches('[data-act], button, input, textarea, select') ? [el] : []
  all.push(...el.querySelectorAll('[data-act], button, input, textarea, select'))
  for (const c of all) {
    if (c.matches('[data-act]') && !c.matches(NATIVE) && !c.hasAttribute('role')) {
      c.setAttribute('role', 'button')
      if (!c.hasAttribute('tabindex')) c.tabIndex = 0
    }
    if (!c.hasAttribute('aria-label')) {
      const text = c.matches('input, textarea, select') ? '' : c.textContent.trim()
      const title = c.getAttribute('title')?.trim()
      const hint = c.getAttribute('placeholder')?.trim()
      if (!text && title) c.setAttribute('aria-label', title)
      else if (!text && c.matches('input, textarea') && !c.labels?.length) { if (title || hint) c.setAttribute('aria-label', title || hint) }
    }
  }
  for (const img of el.querySelectorAll('img:not([alt])')) img.alt = ''
}

export function screenA11y(root) {
  if (!root || root.__a11y) return
  root.__a11y = true
  tidy(root)
  const pending = new Set()
  let queued = false
  new MutationObserver((list) => {
    for (const m of list) {
      if (m.type === 'attributes') { pending.add(m.target); continue }
      for (const n of m.addedNodes) if (n.nodeType === 1) pending.add(n)
    }
    if (queued) return
    queued = true
    queueMicrotask(() => { queued = false; for (const n of pending) if (n.isConnected) tidy(n); pending.clear() })
  }).observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-act', 'title'] })
  // Return or Space on a control that is not a button by birth presses it
  root.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return
    const el = e.target
    if (!(el instanceof HTMLElement) || el.matches(NATIVE) || el.getAttribute('role') !== 'button') return
    e.preventDefault()
    el.click()
  })
}
